import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import '../../services/GlobalVariables';

const initialState = {
    paidLeaves: [],
    allPaidLeaveRequests: [],
    leaveBalance: [],
    loading: false,
    error: null,
    success: false
};

// Get employee's paid leaves
export const getEmpPaidLeaves = createAsyncThunk(
    'empPaidLeaves/getEmpPaidLeaves',
    async (empID, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${global.SET_API_URL}empPaidLeaves/employee/${empID}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch paid leaves');
        }
    }
);

// Get all paid leave requests (for management)
export const getAllPaidLeaveRequests = createAsyncThunk(
    'empPaidLeaves/getAllRequests',
    async (status = null, { rejectWithValue }) => {
        try {
            const url = status
                ? `${global.SET_API_URL}empPaidLeaves/all?status=${status}`
                : `${global.SET_API_URL}empPaidLeaves/all`;
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch requests');
        }
    }
);

// Get employee leave balance
export const getEmployeeLeaveBalance = createAsyncThunk(
    'empPaidLeaves/getBalance',
    async (empID, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${global.SET_API_URL}empPaidLeaves/balance/${empID}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch leave balance');
        }
    }
);

// Apply for paid leave
export const applyPaidLeave = createAsyncThunk(
    'empPaidLeaves/apply',
    async (leaveData, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${global.SET_API_URL}empPaidLeaves/apply`, leaveData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to apply for leave');
        }
    }
);

// Update leave status (Approve/Reject)
export const updateLeaveStatus = createAsyncThunk(
    'empPaidLeaves/updateStatus',
    async ({ paidLeaveID, approvalStatus, approvedBy, approvalRemarks }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                `${global.SET_API_URL}empPaidLeaves/approve/${paidLeaveID}`,
                { approvalStatus, approvedBy, approvalRemarks }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to update leave status');
        }
    }
);

// Update paid leave application
export const updatePaidLeave = createAsyncThunk(
    'empPaidLeaves/update',
    async ({ paidLeaveID, leaveData }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                `${global.SET_API_URL}empPaidLeaves/${paidLeaveID}`,
                leaveData
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to update leave');
        }
    }
);

// Delete paid leave
export const deletePaidLeave = createAsyncThunk(
    'empPaidLeaves/delete',
    async (paidLeaveID, { rejectWithValue }) => {
        try {
            const response = await axios.delete(`${global.SET_API_URL}empPaidLeaves/${paidLeaveID}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to delete leave');
        }
    }
);

const empPaidLeavesSlice = createSlice({
    name: 'empPaidLeaves',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = false;
        }
    },
    extraReducers: (builder) => {
        builder
            // Get employee paid leaves
            .addCase(getEmpPaidLeaves.pending, (state) => {
                state.loading = true;
            })
            .addCase(getEmpPaidLeaves.fulfilled, (state, action) => {
                state.loading = false;
                state.paidLeaves = action.payload;
            })
            .addCase(getEmpPaidLeaves.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Get all paid leave requests
            .addCase(getAllPaidLeaveRequests.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAllPaidLeaveRequests.fulfilled, (state, action) => {
                state.loading = false;
                state.allPaidLeaveRequests = action.payload;
            })
            .addCase(getAllPaidLeaveRequests.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Get leave balance
            .addCase(getEmployeeLeaveBalance.pending, (state) => {
                state.loading = true;
            })
            .addCase(getEmployeeLeaveBalance.fulfilled, (state, action) => {
                state.loading = false;
                state.leaveBalance = action.payload;
            })
            .addCase(getEmployeeLeaveBalance.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Apply for paid leave
            .addCase(applyPaidLeave.pending, (state) => {
                state.loading = true;
                state.success = false;
            })
            .addCase(applyPaidLeave.fulfilled, (state) => {
                state.loading = false;
                state.success = true;
            })
            .addCase(applyPaidLeave.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Update leave status
            .addCase(updateLeaveStatus.pending, (state) => {
                state.loading = true;
                state.success = false;
            })
            .addCase(updateLeaveStatus.fulfilled, (state) => {
                state.loading = false;
                state.success = true;
            })
            .addCase(updateLeaveStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Update paid leave
            .addCase(updatePaidLeave.pending, (state) => {
                state.loading = true;
                state.success = false;
            })
            .addCase(updatePaidLeave.fulfilled, (state) => {
                state.loading = false;
                state.success = true;
            })
            .addCase(updatePaidLeave.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Delete paid leave
            .addCase(deletePaidLeave.pending, (state) => {
                state.loading = true;
                state.success = false;
            })
            .addCase(deletePaidLeave.fulfilled, (state) => {
                state.loading = false;
                state.success = true;
            })
            .addCase(deletePaidLeave.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { clearError, clearSuccess } = empPaidLeavesSlice.actions;
export default empPaidLeavesSlice.reducer;
